/**
 * Live camera self-view in an always-on-top Document Picture-in-Picture window (Chrome/Edge),
 * so presenters see themselves while working in other windows during a recording.
 *
 * The window shows the camera edge to edge; hovering it shows the recording time and
 * Pause/Resume/Stop. The window frame itself belongs to the browser.
 *
 * Opening the window needs a recent user click (transient activation); callers try right after
 * recording starts and otherwise offer a button.
 *
 * Inside ACV the same window is opened by ACV (ACVNNS-Frontend
 * AiRecordingLeaveGuard/AiRecordingCameraPreview.js — keep the look of both in sync).
 */
import type { CameraShape } from './compositor';
import { getAcvParentOriginForPostMessage } from '../../utils/acvParentOrigin';

export type PreviewCommand = 'pause' | 'resume' | 'stop';
export interface PreviewStatus {
  time: string;
  paused: boolean;
}

export interface CameraPreviewWindow {
  setStatus: (status: PreviewStatus) => void;
  close: () => void;
}

interface DocumentPictureInPicture {
  requestWindow: (options?: { width?: number; height?: number }) => Promise<Window>;
  window: Window | null;
}

const pip = (): DocumentPictureInPicture | undefined =>
  (window as unknown as { documentPictureInPicture?: DocumentPictureInPicture }).documentPictureInPicture;

/** Same browser as the host, so the API's presence here tells whether the host can open one. */
export const isCameraPreviewWindowSupported = () => typeof pip()?.requestWindow === 'function';

/**
 * Inside ACV (an iframe) the window can't be opened here: only a top-level page may open a
 * Picture-in-Picture window. ACV opens it on request and reports whether it's open.
 */
export const isEmbedded = () => {
  try {
    return window.parent !== window;
  } catch {
    return true;
  }
};

const HOST_REQUEST = 'airecording-camera-preview';
const HOST_STATE = 'acv-camera-preview-state';
const HOST_COMMAND = 'acv-camera-preview-command';

const postToHost = (message: Record<string, unknown>) => {
  try {
    window.parent.postMessage({ type: HOST_REQUEST, ...message }, getAcvParentOriginForPostMessage() ?? '*');
  } catch {
    // ignore
  }
};

/** Must be called from the click handler that should open it (the click lets ACV open the window). */
export function requestHostCameraPreview(action: 'open' | 'close', shape?: CameraShape) {
  postToHost({ action, shape });
}

/** Keeps the time and pause state shown in ACV's window up to date. */
export function sendHostCameraPreviewStatus(status: PreviewStatus) {
  postToHost({ action: 'status', ...status });
}

/**
 * Subscribes to ACV's window updates: open/closed, and Pause/Resume/Stop clicked in the window.
 * Returns the unsubscribe function.
 */
export function onHostCameraPreview(handlers: {
  /** `cameraError`: ACV's window opened but its camera didn't (permission blocked, no device). */
  onState: (open: boolean, cameraError: boolean) => void;
  onCommand: (command: PreviewCommand) => void;
}): () => void {
  const onMessage = (e: MessageEvent) => {
    if (e.source !== window.parent) return;
    if (e.data?.type === HOST_STATE) handlers.onState(e.data.open === true, e.data.error === 'camera');
    else if (e.data?.type === HOST_COMMAND && ['pause', 'resume', 'stop'].includes(e.data.command)) {
      handlers.onCommand(e.data.command as PreviewCommand);
    }
  };
  window.addEventListener('message', onMessage);
  return () => window.removeEventListener('message', onMessage);
}

const ICONS = {
  pause: '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="5" width="4" height="14" rx="1"/><rect x="14" y="5" width="4" height="14" rx="1"/></svg>',
  resume: '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5.5v13a1 1 0 0 0 1.5.86l10.5-6.5a1 1 0 0 0 0-1.72L9.5 4.64A1 1 0 0 0 8 5.5z"/></svg>',
  stop: '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><rect x="5" y="5" width="14" height="14" rx="2"/></svg>',
};

const STYLES = `
  html, body { margin: 0; height: 100%; background: #000; overflow: hidden;
    font-family: system-ui, -apple-system, "Segoe UI", sans-serif; }
  video { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
  .bar { position: absolute; left: 0; right: 0; bottom: 0; display: flex; align-items: center;
    gap: 8px; padding: 22px 10px 10px; color: #fff; opacity: 0; transition: opacity .15s;
    background: linear-gradient(transparent, rgba(0, 0, 0, .78)); }
  body:hover .bar, .bar:focus-within { opacity: 1; }
  .dot { width: 9px; height: 9px; border-radius: 50%; background: #ef4444; flex: none;
    animation: pulse 1.4s ease-in-out infinite; }
  .paused .dot { background: #f59e0b; animation: none; }
  @keyframes pulse { 50% { opacity: .35; } }
  .time { flex: 1; font: 600 13px ui-monospace, "Cascadia Mono", Consolas, monospace; }
  button { width: 32px; height: 32px; border-radius: 50%; border: 0; cursor: pointer;
    display: flex; align-items: center; justify-content: center; color: #fff;
    background: rgba(255, 255, 255, .2); }
  button:hover { background: rgba(255, 255, 255, .32); }
  button.stop { background: #ef4444; }
  button.stop:hover { background: #dc2626; }`;

export async function openCameraPreviewWindow(
  stream: MediaStream,
  onCommand: (command: PreviewCommand) => void,
  onClosed: () => void,
): Promise<CameraPreviewWindow> {
  const api = pip();
  if (!api) throw new Error('Document Picture-in-Picture is not supported');
  // Only one PiP window per page: reuse the slot.
  api.window?.close();

  const win = await api.requestWindow({ width: 260, height: 260 });
  const doc = win.document;
  doc.title = 'AI Recording';
  const style = doc.createElement('style');
  style.textContent = STYLES;
  doc.head.appendChild(style);

  const video = doc.createElement('video');
  video.autoplay = true;
  video.muted = true;
  video.playsInline = true;
  video.srcObject = stream;

  const bar = doc.createElement('div');
  bar.className = 'bar';
  const dot = doc.createElement('span');
  dot.className = 'dot';
  const time = doc.createElement('span');
  time.className = 'time';
  time.textContent = '00:00';
  const pauseBtn = doc.createElement('button');
  const stopBtn = doc.createElement('button');
  stopBtn.className = 'stop';
  stopBtn.innerHTML = ICONS.stop;
  stopBtn.title = 'Stop';
  stopBtn.setAttribute('aria-label', 'Stop');

  let paused = false;
  const renderPause = () => {
    pauseBtn.innerHTML = paused ? ICONS.resume : ICONS.pause;
    pauseBtn.title = paused ? 'Resume' : 'Pause';
    pauseBtn.setAttribute('aria-label', pauseBtn.title);
    bar.classList.toggle('paused', paused);
  };
  renderPause();
  pauseBtn.addEventListener('click', () => onCommand(paused ? 'resume' : 'pause'));
  stopBtn.addEventListener('click', () => onCommand('stop'));

  bar.append(dot, time, pauseBtn, stopBtn);
  doc.body.append(video, bar);
  video.play().catch(() => { /* autoplay is allowed for muted video */ });

  let closedByUs = false;
  win.addEventListener('pagehide', () => {
    video.srcObject = null;
    if (!closedByUs) onClosed();
  }, { once: true });

  return {
    setStatus: (status) => {
      time.textContent = status.time;
      if (status.paused !== paused) {
        paused = status.paused;
        renderPause();
      }
    },
    close: () => {
      closedByUs = true;
      win.close();
    },
  };
}
